// Chatbot analytics.  /analytics/chatbot
//
// This is the screen section 1.3.1 of the client scope describes. It names six
// signals to capture, and all six are drawn here:
//
//   session duration      session_duration.avg_seconds / median / distribution
//   drop-off points       drop_off_points
//   completion rates      completion_rate, plus per-flow in the table
//   resolution time       resolution_time.avg_seconds
//   user satisfaction     nps  (a 0 to 10 survey, NOT the 4.2/5 the scope asks
//                              for; see the label and the targets panel)
//   most common scam types scam_types
//
// Every tile checks its own denominator before it prints. A rate with nothing
// under the line is "Not measured", never 0% and never 100%, because a
// flattering number for something nobody counted is worse than a blank.

import { http, qs } from "../api";
import { Banner, Empty, SkeletonRows, Table, useAsync, type Column } from "../ui";
import { BarChartCard, DonutCard, StatTile, type BarDatum } from "../charts";
import {
  ExportMenu,
  NotMeasuredTile,
  RangeControls,
  formatSeconds,
  nf,
  useRange,
} from "./shared";
import { ScopeTargetsPanel } from "./scope";

interface DurationBucket {
  bucket: string;
  count: number;
}

interface DropOffPoint {
  step: string;
  flow_name: string;
  count: number;
}

interface ScamTypeCount {
  type: string;
  label: string;
  count: number;
}

interface ChatbotAnalytics {
  period: { from: string; to: string };
  session_duration: { avg_seconds: number; median_seconds: number; distribution: DurationBucket[] };
  drop_off_points: DropOffPoint[];
  completion_rate: { total: number; completed: number; percent: number };
  resolution_time: { avg_seconds: number; completed_count: number };
  nps: {
    total_responses: number;
    detractors: number;
    passives: number;
    promoters: number;
    nps_score: number;
  };
  scam_types: ScamTypeCount[];
  voice_note_usage: { users_with_voice: number; total_users: number; percent: number };
  avg_conversations_per_user: number;
  total_conversations: number;
  unique_users: number;
  other_fraud_llm_calls: number;
}

interface FlowMetric {
  flow_id: string;
  flow_name: string;
  starts: number;
  completions: number;
  completion_rate: number;
  drop_offs: number;
}

interface FlowMetricsResponse {
  period: { from: string; to: string };
  flows: FlowMetric[];
}

export function ChatbotAnalyticsView() {
  // Chatbot analytics opens on 30 days, and a half-filled custom range falls
  // back to 30 days too, not to the month like every other screen here.
  const { preset, setPreset, draft, setDraft, apply, range } = useRange({
    initial: "30days",
    fallback: "30days",
  });

  const query = useAsync(
    () =>
      Promise.all([
        http.get<ChatbotAnalytics>(`/analytics/chatbot${qs({ from: range.from, to: range.to })}`),
        http.get<FlowMetricsResponse>(
          `/analytics/chatbot/by-flow${qs({ from: range.from, to: range.to })}`
        ),
      ]).then(([analytics, flows]) => ({ analytics, flows: flows.flows ?? [] })),
    [range.from, range.to]
  );

  const data = query.data?.analytics ?? null;
  const flows = query.data?.flows ?? [];

  const durationBars: BarDatum[] = (data?.session_duration.distribution ?? []).map((b) => ({
    label: b.bucket,
    value: b.count,
  }));

  // The denominators. Each one decides whether its tile prints a figure at all.
  const timedSessions = durationBars.reduce((sum, b) => sum + b.value, 0);
  const terminalSessions = data?.completion_rate.total ?? 0;
  const resolvedSessions = data?.resolution_time.completed_count ?? 0;
  const npsResponses = data?.nps.total_responses ?? 0;
  const voiceUsers = data?.voice_note_usage.total_users ?? 0;

  const scamSlices: BarDatum[] = (data?.scam_types ?? []).map((s) => ({
    label: s.label,
    value: s.count,
  }));
  const scamMax = Math.max(0, ...(data?.scam_types ?? []).map((s) => s.count));

  const dropOffColumns: Column<DropOffPoint>[] = [
    { key: "step", header: "Step", sortable: true },
    {
      key: "flow_name",
      header: "Flow",
      sortable: true,
      render: (r) => r.flow_name || <span className="muted">—</span>,
    },
    {
      key: "count",
      header: "Sessions",
      align: "right",
      sortable: true,
      render: (r) => <span className="num">{nf.format(r.count)}</span>,
    },
  ];

  const flowColumns: Column<FlowMetric>[] = [
    { key: "flow_name", header: "Flow", sortable: true },
    {
      key: "starts",
      header: "Starts",
      align: "right",
      sortable: true,
      render: (r) => <span className="num">{nf.format(r.starts)}</span>,
    },
    {
      key: "completions",
      header: "Completions",
      align: "right",
      sortable: true,
      render: (r) => (
        <span className="num">
          {nf.format(r.completions)}
          {/* No starts means no rate, not a 0% rate. */}
          {r.starts > 0 && <span className="muted"> ({r.completion_rate.toFixed(1)}%)</span>}
        </span>
      ),
    },
    {
      key: "drop_offs",
      header: "Drop-offs",
      align: "right",
      sortable: true,
      render: (r) => <span className="num">{nf.format(r.drop_offs)}</span>,
    },
  ];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Chatbot analytics</h1>
          <div className="sub">Sessions, completion, scam mix and per-flow performance.</div>
        </div>
        <div className="page-actions">
          <RangeControls
            preset={preset}
            onPreset={setPreset}
            draft={draft}
            onDraft={setDraft}
            onApply={apply}
          />
          <ExportMenu snapshot="chatbot" range={range} rawDataset="chatbot_sessions" />
        </div>
      </div>

      {query.error && (
        <Banner tone="critical" traceId={query.error.traceId}>
          Could not load chatbot analytics: {query.error.message}
        </Banner>
      )}

      <ScopeTargetsPanel nps={data?.nps ?? null} loading={query.loading} />

      {query.loading ? (
        <div className="grid c4">
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div className="card" key={i}>
              <SkeletonRows n={2} cols={2} />
            </div>
          ))}
        </div>
      ) : !data ? (
        <Empty title="No sessions in this window" body="Widen the range to see the funnel." icon="◕" />
      ) : (
        <>
          <div className="grid c4" style={{ marginBottom: "var(--sp-4)" }}>
            <StatTile label="Total conversations" value={data.total_conversations} />

            {terminalSessions > 0 ? (
              <StatTile
                label="Completion rate"
                value={`${data.completion_rate.percent}%`}
                deltaLabel={`${nf.format(data.completion_rate.completed)} of ${nf.format(
                  terminalSessions
                )} terminal sessions`}
              />
            ) : (
              <NotMeasuredTile
                label="Completion rate"
                why="no session reached a terminal state here"
              />
            )}

            {timedSessions > 0 ? (
              <StatTile
                label="Avg session duration"
                value={formatSeconds(data.session_duration.avg_seconds)}
                deltaLabel={`median ${formatSeconds(
                  data.session_duration.median_seconds
                )} · ${nf.format(timedSessions)} closed sessions`}
              />
            ) : (
              <NotMeasuredTile
                label="Avg session duration"
                why="no closed session carried a completion time"
              />
            )}

            {/* Labelled for what it is. The scope asks for satisfaction out of
                5; this survey asks a 0 to 10 NPS question. */}
            {npsResponses > 0 ? (
              <StatTile
                label="NPS (0 to 10 survey)"
                value={data.nps.nps_score}
                deltaLabel={`${nf.format(npsResponses)} responses · not a score out of 5`}
              />
            ) : (
              <NotMeasuredTile label="NPS (0 to 10 survey)" why="no survey responses in this period" />
            )}
          </div>

          <div className="grid c4" style={{ marginBottom: "var(--sp-4)" }}>
            <StatTile label="Unique users" value={data.unique_users} />

            {resolvedSessions > 0 ? (
              <StatTile
                label="Avg resolution time"
                value={formatSeconds(data.resolution_time.avg_seconds)}
                deltaLabel={`${nf.format(resolvedSessions)} resolved sessions`}
              />
            ) : (
              <NotMeasuredTile label="Avg resolution time" why="no session resolved in this period" />
            )}

            {data.unique_users > 0 ? (
              <StatTile
                label="Avg conversations per user"
                value={data.avg_conversations_per_user}
              />
            ) : (
              <NotMeasuredTile label="Avg conversations per user" why="no user started a session" />
            )}

            {voiceUsers > 0 ? (
              <StatTile
                label="Voice-note users"
                value={`${data.voice_note_usage.percent}%`}
                deltaLabel={`${nf.format(data.voice_note_usage.users_with_voice)} of ${nf.format(
                  voiceUsers
                )}`}
              />
            ) : (
              <NotMeasuredTile label="Voice-note users" why="nobody sent an inbound message" />
            )}
          </div>

          <div className="grid c2" style={{ marginBottom: "var(--sp-4)" }}>
            <div className="card">
              {timedSessions === 0 ? (
                <Empty
                  title="Session duration not measured"
                  body="No closed session in this window carried both a start and a completion time."
                />
              ) : (
                <BarChartCard
                  title="Session duration distribution"
                  data={durationBars}
                  yLabel="Sessions"
                  note="Closed sessions only."
                />
              )}
            </div>
            <div className="card">
              {data.other_fraud_llm_calls > 0 && (
                <div className="muted" style={{ marginBottom: "var(--sp-3)" }}>
                  ✦ {nf.format(data.other_fraud_llm_calls)} LLM calls served the "other fraud" path.
                </div>
              )}
              {scamSlices.length === 0 ? (
                <Empty title="No scam-classified sessions" body="Nothing was classified in this window." />
              ) : (
                <>
                  <DonutCard
                    title="Scam types"
                    data={scamSlices}
                    note="Top classifications by session count."
                    centerCaption="sessions"
                  />
                  {/* The donut folds its tail into "Other" at six slices; this
                      ranked list keeps every classification the server returned. */}
                  <div className="col" style={{ marginTop: "var(--sp-4)" }}>
                    {(data.scam_types ?? []).map((s) => (
                      <div key={s.type}>
                        <div className="row">
                          <span className="truncate">{s.label}</span>
                          <span className="spacer" />
                          <span className="num muted">{nf.format(s.count)} sessions</span>
                        </div>
                        <div className="meter" style={{ marginTop: 4 }}>
                          <span
                            style={{ width: scamMax > 0 ? `${(s.count / scamMax) * 100}%` : "0%" }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="card flush" style={{ marginBottom: "var(--sp-4)" }}>
            <div className="card-head">
              <h3>Drop-off points</h3>
              <span className="muted">Where sessions were abandoned</span>
            </div>
            <Table
              columns={dropOffColumns}
              rows={data.drop_off_points ?? []}
              empty="No drop-offs recorded in this window"
            />
          </div>

          <div className="card flush">
            <div className="card-head">
              <h3>Flow performance</h3>
              <span className="muted">Scam flows only</span>
            </div>
            <Table columns={flowColumns} rows={flows} empty="No flow activity in this window" />
          </div>
        </>
      )}
    </div>
  );
}
